import axios from "axios";

import { apiClient } from "./client";

/** One section on a page. A controlled shape — never arbitrary markup. */
export type WebsiteSection = {
  id: string;
  type: string;
  variant: string;
  visible: boolean;
  settings: Record<string, unknown>;
  content: Record<string, unknown>;
};

export type WebsitePage = {
  type: string;
  slug: string;
  title: string;
  /** JSON string of WebsiteSection[] as stored. */
  sections: string;
};

export type Website = {
  id: string;
  /** Slug công khai hiệu lực (custom slug nếu có, không thì tenants.code viết thường). */
  slug: string;
  /** Slug tùy chỉnh phòng khám tự đặt (ADR-0040); null = đang dùng mã tenant. */
  customSlug?: string | null;
  /**
   * URL trang web addy-sites (ADR-0040) — có khi đã xuất bản + cấu hình template; null thì client
   * dùng lại preview built-in /site/&lt;slug&gt; của HIS.
   */
  publicUrl?: string | null;
  themeId: string;
  status: "DRAFT" | "PUBLISHED";
  goals: string;
  branding: string;
  seo: string;
  publishedAt: string | null;
  pages: WebsitePage[];
};

/**
 * Bảng màu của một theme, đúng như addy-sites công bố ở `/api/themes` (#19568). Tuỳ chọn: bản dự
 * phòng của HIS (khi Sites không trả lời) không có màu, và thẻ xem trước khi đó vẽ màu trung tính.
 */
export type AddyThemeTokens = {
  brand?: string;
  brand2?: string;
  accent?: string;
  bg?: string;
  surface?: string;
  ink?: string;
};

/** A theme from the addy-sites catalog (ADR-0040) — the catalog HIS /website picks from. */
export type AddyTheme = {
  key: string;
  name: string;
  description?: string;
  tokens?: AddyThemeTokens;
};

/**
 * Whether the website holds the key HIS considers valid (#19003). UNKNOWN = Addy Sites could not be
 * asked — never shown as "not synced".
 */
export type GatewaySiteSync = "SYNCED" | "NOT_SYNCED" | "NOT_PROVISIONED" | "UNKNOWN";

/** Gateway connection status (ADR-0040 Phase 3) — whether a per-tenant API key is active. Never the key. */
export type WebsiteConnection = {
  tenantId: string;
  connected: boolean;
  clientId: string;
  scopes: string[];
  createdAt: string | null;
  rotatedAt: string | null;
  siteSync: GatewaySiteSync;
};

/**
 * Why the website cannot offer the results lookup (#19138). The same answer the addy-sites gateway
 * reads before it shows the form, so this screen and the website never disagree.
 */
export type ResultsLookupReason =
  | "NOT_SUPPORTED_BY_HIS"
  | "DISABLED_BY_CLINIC"
  | "OTP_ACTION_DISABLED"
  | "MESSAGING_NOT_CONFIGURED"
  | "OTP_TEMPLATE_NOT_READY"
  | "OTP_CHECK_UNAVAILABLE";

/** #19180 — one OTP channel's verdict; `problem` is Vietnamese, written for the clinic. */
export type OtpChannelCheck = {
  channel: string;
  templateCode: string | null;
  ready: boolean;
  problem: string | null;
};

/** One availability answer: what the website sees, or the OTP half alone. */
export type LookupAvailability = {
  available: boolean;
  reason: ResultsLookupReason | null;
  /** #19180 — available, but only over Zalo: a patient without Zalo gets no code. */
  warning?: "OTP_ZBS_ONLY" | null;
  otpChannels?: OtpChannelCheck[];
  /** #19612 — the clinic's "Mã xác thực" switch; absent when the OTP was not examined. */
  otpActionEnabled?: boolean | null;
  /** #19612 — the clinic has an Addy Message credential; absent likewise. */
  messagingConfigured?: boolean | null;
};

/** The clinic's switch + what the website actually sees. */
export type ResultsLookup = {
  enabled: boolean;
  examResults: LookupAvailability;
  /**
   * #19612 — the OTP half alone, whatever the switch says. While the switch is off `examResults`
   * only says DISABLED_BY_CLINIC; this is what tells the screen, before the clinic ticks it,
   * whether the save would be refused and why. Absent on an older API.
   */
  otp?: LookupAvailability;
};

export type HisSnapshot = {
  services?: { id: string; name: string; description?: string; category?: string; durationMinutes?: number; priceVnd?: number | null }[];
  doctors?: { id: string; name: string; title?: string; specialty?: string; bio?: string }[];
  specialties?: { id: string; name: string }[];
  branches?: { id: string; name: string; address?: string; phone?: string }[];
};

export const websiteApi = {
  /** null when the clinic has not created a website yet (the API 404s by design). */
  get: async (): Promise<Website | null> => {
    try {
      return (await apiClient.get<Website>("/v1/website")).data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  },

  create: (themeId: string, goals: string[]) =>
    apiClient.post<Website>("/v1/website", { themeId, goals }).then((r) => r.data),

  /**
   * Đổi giao diện. Từ #19459 lời gọi này ra tới addy-sites thật (`WebsiteService.changeTheme` gửi
   * khoá kèm sự kiện hồ sơ), nên website và Zalo Mini App đổi theo — trước đó nó chỉ ghi
   * `clinic_websites.theme_id` của HIS và không ai bên ngoài thấy gì.
   */
  changeTheme: (themeId: string) =>
    apiClient.put<Website>("/v1/website/theme", { themeId }).then((r) => r.data),

  /**
   * The themes addy-sites offers (ADR-0040) — HIS picks from the same list the Astro site renders.
   *
   * `recommendedKey` is the theme that suits the vertical the clinic already chose, so Bước 1 can
   * open on it instead of an empty picker; `recommendedReason` is that vertical's label, or "" when
   * the clinic has no vertical set and the key is just the fallback.
   *
   */
  availableThemes: () =>
    apiClient
      .get<{
        themes: AddyTheme[];
        source?: string;
        recommendedKey?: string;
        recommendedReason?: string;
      }>("/v1/website/available-themes")
      .then((r) => r.data),

  /** Check a candidate custom slug: valid format + globally available (ADR-0040). */
  slugCheck: (slug: string) =>
    apiClient
      .get<{ slug: string; valid: boolean; available: boolean }>("/v1/website/slug-check", {
        params: { slug },
      })
      .then((r) => r.data),

  /** Set (or clear with "") the custom public slug. */
  setSlug: (slug: string) =>
    apiClient.put<Website>("/v1/website/slug", { slug }).then((r) => r.data),

  saveSections: (slug: string, sections: WebsiteSection[]) =>
    apiClient.put<void>(`/v1/website/pages/${slug || "home"}/sections`, {
      sections: JSON.stringify(sections),
    }),

  publish: (note?: string) =>
    apiClient.post<Website>("/v1/website/publish", { note }).then((r) => r.data),

  unpublish: () => apiClient.post<Website>("/v1/website/unpublish").then((r) => r.data),

  /** Live HIS data, so the editor previews exactly what a patient will see. */
  data: () => apiClient.get<HisSnapshot>("/v1/website/data").then((r) => r.data),

  /** Gateway connection status (ADR-0040 Phase 3) — is a per-tenant API key active? */
  connection: () =>
    apiClient.get<WebsiteConnection>("/v1/website/connection").then((r) => r.data),

  /**
   * Kết nối lại: rotate the gateway API key and hand it to Addy Sites. Returns the status — the key
   * goes server to server and never reaches the browser (#19003).
   */
  connect: () =>
    apiClient.post<WebsiteConnection>("/v1/website/connection").then((r) => r.data),

  /** Tra cứu kết quả khám trên website (#19138): công tắc của phòng khám + trạng thái thực tế. */
  resultsLookup: () =>
    apiClient.get<ResultsLookup>("/v1/website/results-lookup").then((r) => r.data),

  setResultsLookup: (enabled: boolean) =>
    apiClient
      .put<ResultsLookup>("/v1/website/results-lookup", { enabled })
      .then((r) => r.data),

  /** Ngắt kết nối: revoke the gateway API key. */
  disconnect: () =>
    apiClient.delete<WebsiteConnection>("/v1/website/connection").then((r) => r.data),

  /** Quản lý website: mint an SSO redirect URL so staff manage the addy-sites CMS as Owner (no 2nd login). */
  ssoLink: (surface?: "miniapp") =>
    apiClient
      .get<{ url: string }>("/v1/website/connection/sso-link", {
        params: surface ? { surface } : undefined,
      })
      .then((r) => r.data),
};

export function parseSections(page: WebsitePage | undefined): WebsiteSection[] {
  if (!page) return [];
  try {
    const parsed = JSON.parse(page.sections);
    return Array.isArray(parsed) ? (parsed as WebsiteSection[]) : [];
  } catch {
    return [];
  }
}
